import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

type VerifyState = 'loading' | 'success' | 'already_verified' | 'error';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();
  const [state, setState] = useState<VerifyState>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMessage('No verification token provided.');
      return;
    }

    let cancelled = false;

    verifyEmail(token)
      .then((res) => {
        if (cancelled) return;
        if (res.message?.includes('already')) {
          setState('already_verified');
        } else {
          setState('success');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState('error');
        setErrorMessage(
          err instanceof Error ? err.message : 'Verification failed. The link may be invalid or expired.',
        );
      });

    return () => { cancelled = true; };
  }, [token, verifyEmail]);

  return (
    <div className="bg-background">
      <section className="relative py-24 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15" style={{ backgroundImage: "url('/images/goldson-wade-2018-hero.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800/90 to-primary-dark/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-hero text-white mb-6 max-w-4xl mx-auto">Email Verification</h1>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          {state === 'loading' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-slate-600">Verifying your email...</p>
            </div>
          )}

          {state === 'success' && (
            <div className="rounded-lg bg-success-light border border-success-border p-8 text-center">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">Email Verified!</h2>
              <p className="text-slate-600 text-sm mb-6">
                Your email has been verified. You can now log in to your account.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-lg font-bold hover:bg-primary-hover transition-colors"
              >
                Go to Login
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          )}

          {state === 'already_verified' && (
            <div className="rounded-lg bg-info-light border border-info-border p-8 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">Already Verified</h2>
              <p className="text-slate-600 text-sm mb-6">
                Your email is already verified. You can log in to your account.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-lg font-bold hover:bg-primary-hover transition-colors"
              >
                Go to Login
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          )}

          {state === 'error' && (
            <div className="rounded-lg bg-error-light border border-error-border p-8 text-center">
              <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">Verification Failed</h2>
              <p className="text-slate-600 text-sm mb-6">{errorMessage}</p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
              >
                Back to Register
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
